import { AvatarUpdateDialogComponent } from "@/components/avatar-update-dialog/avatar-update-dialog.component";
import { BasePageComponent } from "@/components/base/BasePageComponent";
import { ConfirmDialogComponent } from "@/components/confirm-dialog/confirm-dialog.component";
import { IframeDialogComponent } from "@/components/iframe-dialog/iframe-dialog.component";
import { PasswordUpdateDialogComponent } from "@/components/password-update-dialog/password-update-dialog.component";
import { PdfViewerDialogComponent } from "@/components/pdf-viewer-dialog/pdf-viewer-dialog.component";
import { ProfileUpdateDialogComponent } from "@/components/profile-update-dialog/profile-update-dialog.component";
import { SubscriptionChoiceDialogComponent } from "@/components/subscription-choice-dialog/subscription-choice-dialog.component";
import { DateTimeFormatPipe } from "@/extensions/dateTimePipe";
import { AddressHelper } from "@/helpers/addressHelper";
import { PropertyHelper } from "@/helpers/property.helper";
import { UserHelper } from "@/helpers/user.helper";
import { AlertService } from "@/services/alert.service";
import { AuthenticationService } from "@/services/authentication.service";
import { NavService } from "@/services/nav.service";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import { MatTooltipModule } from "@angular/material/tooltip";
import { ActivatedRoute } from "@angular/router";
import {
  BusinessManagementService,
  Document,
  IdentityProviderType,
  Payment,
  PaymentStatus,
  SubscriptionManagementType,
  SubscriptionPlanType,
  SubscriptionState,
  User,
  UserPreferences,
  UserRole,
  UserSession,
  UsersService,
  VaultService,
} from "@eemo/api-client";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { CardBaseComponent } from "../../components/base/card-base/card-base.component";
import { DateFormatPipe } from "../../extensions/datePipe";

import { InfoDialogComponent } from "@/components/info-dialog/info-dialog.component";
import { PreferencesUpdateDialogComponent } from "@/components/preferences-update-dialog/preferences-update-dialog.component";
import { UserDeleteDialogComponent } from "@/components/user-delete-dialog/user-delete-dialog.component";
import { AppConfigService } from "@/services/app-config.service";
import { CurrencyPipe, NgFor, NgIf, NgTemplateOutlet } from "@angular/common";
import { MatButtonModule } from "@angular/material/button";
import { ImgDisplayComponent } from "../../components/img-display/img-display.component";
import { PageTitleComponent } from "../../components/page-title/page-title.component";

interface InvoiceDocument {
  url: string;
  filename: string;
  document: Document;
}

@Component({
  selector: "app-profile",
  templateUrl: "./profile.component.html",
  styleUrls: ["./profile.component.scss"],
  standalone: true,
  imports: [
    PageTitleComponent,
    NgIf,
    NgFor,
    ImgDisplayComponent,
    MatButtonModule,
    MatIconModule,
    CardBaseComponent,
    NgTemplateOutlet,
    MatTooltipModule,
    MatTableModule,
    CurrencyPipe,
    TranslateModule,
    DateFormatPipe,
    DateTimeFormatPipe,
  ],
})
export class ProfileComponent
  extends BasePageComponent
  implements OnInit, OnDestroy
{
  currentUser: UserSession;
  user: User;
  hasDefaultAvatar = true;
  avatarTs = UserHelper.DEFAULT_AVATAR_TS;
  currentUserHasActiveSubscription = false;
  userDisplayName: string;
  addresseDisplay: string;

  preferences: UserPreferences;

  externalManagedUser = false;
  canUpdatePassword = true;
  canManagePreferences = false;
  canSeeSubscription = false;
  canSubscribe = false;
  canManageSubscription = false;
  canDeleteAccount = true;
  isOfferedPlan = false;
  subs: Subscription[] = [];
  isRedirectingToStripeSubscription = false;
  isRedirectingToStripePayment = false;
  showSubscribe = false;
  canSeeDefaultVisitModeType = false;

  subscriptionPlanTypeName = "";
  companyMainDisplay = "";
  companyMainTitle = "";
  companyOthersTitle = "";
  companyOthers: string[];

  displayedColumns = ["date", "amountTaxIncl", "status", "actions"];
  dataSource: MatTableDataSource<Payment>;
  hasPayments = false;

  invoices: { [key: string]: InvoiceDocument } = {};

  constructor(
    private authenticationService: AuthenticationService,
    private userService: UsersService,
    public userHelper: UserHelper,
    private alertService: AlertService,
    private dialog: MatDialog,
    protected route: ActivatedRoute,
    protected navService: NavService,
    private activatedRoute: ActivatedRoute,
    private businessService: BusinessManagementService,
    private propHelper: PropertyHelper,
    private vaultService: VaultService,
    private translate: TranslateService,
    private appConfigService: AppConfigService
  ) {
    super(route, navService);
    this.subs.push(
      this.activatedRoute.queryParams.subscribe((values) => {
        if (values["payment"] === "success") {
          this.alertService.success("Paiement effecué avec succès");

          //reload user session
          this.authenticationService.refreshUserSession();
        } else if (values["payment"] === "canceled") {
          this.alertService.warning("Paiement annulé");
        }
        if (values["showSubscribe"]) {
          this.showSubscribe = true;
        }
      })
    );
  }

  ngOnInit() {
    this.subs.push(
      this.authenticationService.currentUser.subscribe((value) => {
        if (value) {
          this.currentUser = value;
          this.loadData();
        }
      })
    );
    this.subs.push(
      this.appConfigService.userPreferencesObs.subscribe(
        (value) => (this.preferences = value)
      )
    );
  }

  loadData() {
    this.subs.push(
      this.userService.getUser(this.currentUser.userId).subscribe((user) => {
        this.user = user;
        if (this.user.emailValidated) {
          // manage email validation issue when the validation is made from another browser page
          this.authenticationService.validateUserEmail();
        }
        this.userDisplayName = UserHelper.userDisplayName(user);
        this.addresseDisplay = AddressHelper.getFullAddress(user.address);
        this.avatarTs = user.avatarTs;
        this.hasDefaultAvatar = this.userHelper.hasDefaultAvatar(user);
        this.externalManagedUser = IdentityProviderType.B2C !== this.user.idp;
        this.canUpdatePassword = !this.externalManagedUser;
        this.canSeeDefaultVisitModeType = [
          UserRole.ADMIN,
          UserRole.REAL_ESTATE,
        ].includes(user.role);
        // for now home view preferences is accessible only for admin, real estate and notary
        this.canManagePreferences = [
          UserRole.ADMIN,
          UserRole.REAL_ESTATE,
          UserRole.NOTARY,
        ].includes(user.role);
        this.canSeeSubscription = UserRole.REAL_ESTATE === user.role;
        this.canManageSubscription = this.canSubscribe =
          UserRole.REAL_ESTATE === user.role &&
          (SubscriptionPlanType.TRIAL === user.currentSubscription?.planType ||
            SubscriptionState.CANCELED === user.currentSubscription?.state);
        this.canManageSubscription =
          UserRole.REAL_ESTATE === user.role &&
          SubscriptionManagementType.STRIPE ===
            user.currentSubscription?.managementType &&
          SubscriptionState.CANCELED !== user.currentSubscription?.state;
        this.currentUserHasActiveSubscription =
          SubscriptionState.ACTIVE === user.currentSubscription?.state ||
          SubscriptionState.TRIALING === user.currentSubscription?.state;
        this.canDeleteAccount =
          SubscriptionManagementType.STRIPE !==
            user.currentSubscription?.managementType ||
          !this.currentUserHasActiveSubscription;
        this.isOfferedPlan =
          SubscriptionPlanType.OFFERED === user.currentSubscription?.planType;

        if (user.currentSubscription) {
          this.subscriptionPlanTypeName = this.translate.instant(
            "enum.SubscriptionPlanType." +
              (this.isOfferedPlan && this.user.companies?.length > 0
                ? "AGENCY"
                : user.currentSubscription.planType)
          );
        }

        this.loadUserPreferences();

        //check if there is a sync problem with session state
        if (
          this.user.currentSubscription &&
          this.currentUser.subscriptionState !==
            this.user.currentSubscription?.state
        ) {
          //reload the session
          this.authenticationService.refreshUserSession();
        }

        if (this.showSubscribe) {
          //show only once
          this.onSubmitMySubscription();
          this.showSubscribe = false;
        }

        if (user.companies.length > 1) {
          this.companyOthersTitle = "Agences secondaires";
          this.companyOthers = [];
        }
        user.companies.forEach((c) => {
          if (c.main) {
            this.companyMainDisplay = c.company.name;
            this.companyMainTitle =
              user.companies.length > 1 ? "Agence principale" : "Agence";
          } else {
            this.companyOthers.push(c.company.name);
          }
        });
      })
    );
    this.subs.push(
      this.businessService
        .getPaymentList(null, null, null, null, null, 0, 3)
        .subscribe((data) => {
          const displayedData = [];
          data.list.forEach((i: Payment) => {
            if (i.status === PaymentStatus.CANCELED) return;
            const row = {
              payment: i,
              date: i.date,
              amountTaxIncl: i.amountTaxIncl,
              isSuccess: i.status === PaymentStatus.SUCCESS,
              invoiceDocumentId: i.invoiceDocumentId,
            };
            if (i.invoiceDocumentId) {
              this.subs.push(
                this.vaultService
                  .getDocument(i.invoiceDocumentId)
                  .subscribe((d) => {
                    this.invoices[d.uid] = {
                      url: this.propHelper.getDocumentUrl(d),
                      filename: d.name + ".pdf",
                      document: d,
                    };
                  })
              );
            }
            displayedData.push(row);
          });
          this.dataSource = new MatTableDataSource(displayedData);
          this.hasPayments = displayedData.length > 0;
        })
    );
  }

  loadUserPreferences() {
    this.subs.push(
      this.userService
        .getUserPreferences(this.currentUser.userId)
        .subscribe((p) => (this.preferences = p))
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  updateProfile(showSubscriptionMessage = false) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      this.dialog.open(ProfileUpdateDialogComponent, {
        data: { user: this.user, showSubscriptionMessage },
      });
    }
  }

  updatePreferences() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      this.dialog.open(PreferencesUpdateDialogComponent, {
        data: { user: this.user },
      });
    }
  }

  confirmSendValidationMail() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        data: { message: "Envoyer à nouveau le mail de validation ?" },
      });

      this.subs.push(
        dialogRef.afterClosed().subscribe((result) => {
          if (result) {
            this.sendValidationMail();
          }
        })
      );
    }
  }

  sendValidationMail() {
    this.subs.push(
      this.userService
        .sendValidateUserEmail(this.currentUser.userId)
        .subscribe(() => {
          this.alertService.success("Email envoyé");
        })
    );
  }

  updatePassword() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      this.dialog.open(PasswordUpdateDialogComponent, {
        // width: '20rem'
      });
    }
  }

  updateAvatar() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(AvatarUpdateDialogComponent, {
        // width: '20rem'
        data: { user: this.user, avatarTs: this.avatarTs },
      });

      this.subs.push(
        dialogRef.afterClosed().subscribe((result) => {
          if (result) {
            this.hasDefaultAvatar = false;
            this.avatarTs = Date.now().toString();
            this.propagateAvatarUpdate();
          }
        })
      );
    }
  }

  deleteAvatar() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        data: { message: "Confirmez-vous la suppression de votre avatar ?" },
      });

      this.subs.push(
        dialogRef.afterClosed().subscribe((result) => {
          if (result) {
            this.doDeleteAvatar();
          }
        })
      );
    }
  }

  doDeleteAvatar() {
    this.subs.push(
      this.userService
        .deleteUserAvatar(this.currentUser.userId)
        .subscribe(() => {
          this.alertService.success("Avatar supprimé");
          this.hasDefaultAvatar = true;
          this.avatarTs = UserHelper.DEFAULT_AVATAR_TS;
          this.propagateAvatarUpdate();
        })
    );
  }

  propagateAvatarUpdate(): void {
    this.subs.push(
      this.userService
        .getUserAvatar(this.user.uid, this.avatarTs)
        .subscribe((file) => this.authenticationService.saveCurrentAvatar(file))
    );
  }

  onSubmitMySubscription() {
    if (!this.user.address) {
      this.updateProfile(true);
    } else {
      if (this.canManageSubscription) {
        this.isRedirectingToStripeSubscription = true;
        this.goToUserPortal();
      } else {
        this.choosePlan();
      }
    }
  }

  onSubmitMyPayments() {
    if (this.canManageSubscription) {
      this.isRedirectingToStripePayment = true;
      this.goToUserPortal();
    }
  }

  goToUserPortal(): void {
    this.subs.push(
      this.businessService.createStripePortalSession().subscribe((p) => {
        window.location.href = p.url;
      })
    );
  }

  choosePlan(): void {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      this.dialog.open(SubscriptionChoiceDialogComponent, {
        data: { user: this.user },
      });
    }
  }

  viewInvoice(invoice: InvoiceDocument) {
    if (this.propHelper.isDocumentViewableInPdfViewer(invoice.document)) {
      this.viewInvoiceInPdfViewer(invoice.document);
    } else if (this.propHelper.isDocumentViewableInIFrame(invoice.document)) {
      this.viewInvoiceInIFrame(invoice.document);
    }
  }

  viewInvoiceInPdfViewer(doc: Document) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      this.dialog.open(PdfViewerDialogComponent, {
        data: { doc },
      });
    }
  }

  viewInvoiceInIFrame(doc: Document) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      this.dialog.open(IframeDialogComponent, {
        data: {
          title: doc.name,
          url: this.propHelper.getDocumentUrl(doc),
          isImage: false,
        },
      });
    }
  }

  deleteAccount() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      this.dialog.open(UserDeleteDialogComponent, {
        data: { user: this.user },
      });
    }
  }

  infoActiveStripeSubscription() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      this.dialog.open(InfoDialogComponent, {
        data: {
          title: "Votre abonnemment est actif",
          content:
            'Vous devez annuler votre abonnement avant de pouvoir supprimer votre compte, en allant sur "Gérer mon abonnement"',
        },
      });
    }
  }
}
